import { useMemo } from 'react'
import { Outlet } from '@tanstack/react-router'
import { getCookie } from '@/lib/cookies'
import { cn } from '@/lib/utils'
import { LayoutProvider } from '@/context/layout-provider'
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { AppSidebar } from '@/components/layout/app-sidebar'
import { SkipToMain } from '@/components/skip-to-main'

type AppLayoutProps = {
    children?: React.ReactNode
}

export function AppLayout({ children }: AppLayoutProps) {
    // 使用 React.useMemo 稳定 defaultOpen 的值
    // 由于 getCookie 是一个纯函数（对于同一 cookie 键值返回相同结果），我们可以将其记忆化
    const defaultOpen = useMemo(() => getCookie('sidebar_state') !== 'false', [])

    // 将所有不变的组件放在记忆化的部分
    return useMemo(
        () => (
            <LayoutProvider>
                <SidebarProvider defaultOpen={defaultOpen}>
                    <SkipToMain />
                    <AppSidebar />
                    <SidebarInset
                        className={cn(
                            // Set content container, so we can use container queries
                            '@container/content',

                            // If layout is fixed, set the height
                            // to 100svh to prevent overflow
                            'has-data-[layout=fixed]:h-svh',

                            // If layout is fixed and sidebar is inset,
                            // set the height to 100svh - spacing (total margins) to prevent overflow
                            'peer-data-[variant=inset]:has-data-[layout=fixed]:h-[calc(100svh-(var(--spacing)*4))]'
                        )}
                    >
                        {/* 动态内容单独传递，保持静态部分记忆化 */}
                        {children ?? <Outlet />}
                    </SidebarInset>
                </SidebarProvider>
            </LayoutProvider>
        ),
        [defaultOpen, children]
    )
}
